import {
  ProductAlert,
  SortSpecification,
  RawAlert,
  PaginatedData,
  User,
  UserType,
  Product,
  ProductInfo,
  FileType,
  ToastState,
} from 'types';
import { ExportMode } from 'helpers/export';
import Toast from '../components/Toast';
import download from 'downloadjs';
import { Omit } from 'react-router';
import { baseURL, addAuthentication, handleErrors } from 'services/ApiService';
import axios from 'axios';
const urljoin = require('url-join');

/* In modern TypeScript, the below can be done in a much more DRY way:
const alertSortKeys = ['alertNumber', 'active', 'created', 'expiryDate'] as const;
type AlertSortAllowedKeys = typeof alertSortKeys[number];
const alertSortAllowedKeys = new Set(alertSortKeys); */
type AlertSortAllowedKey = 'alertNumber' | 'active' | 'created' | 'expiryDate';
const alertSortAllowedKeys: ReadonlySet<string> = new Set([
  'alertNumber',
  'active',
  'created',
  'expiryDate',
]);
type AlertSortSpecification = SortSpecification<
  Pick<ProductAlert, AlertSortAllowedKey>
>;
type FetchAlertParams = {
  page: number;
  pageSize?: number;
  status?: 'active' | 'inactive';
  sort?: AlertSortSpecification;
  includeItems?: boolean;
  filters?: { id: string; value: any }[] | null;
  hideArchived?: boolean;
};

type FetchAlertFileParams = FetchAlertParams & {
  fileType: FileType;
  mode: ExportMode;
  hideArchived: boolean;
};

const requester = axios.create({
  baseURL,
});

requester.interceptors.request.use(addAuthentication);
requester.interceptors.request.use(undefined, (error) => {
  if (error.response && error.response.status === 409) {
    return Promise.reject(error);
  } else {
    return handleErrors(error);
  }
});

const ALERTS_PATH = '/alerts';
const PRODUCTS_PATH = '/products';
async function getAlerts(
  { sort, filters, ...params }: FetchAlertParams,
  user: User
): Promise<PaginatedData<ProductAlert>> {
  let sortParam: string | undefined;
  if (sort) {
    /* The sort parameter is provided to the backend as a comma separated list of sort directions, such as:
        'active asc,expiry_date desc,alert_number asc' */
    sortParam = Array.from({
      *[Symbol.iterator]() {
        for (const [key, direction] of sort.entries()) {
          yield `${key} ${direction}`;
        }
      },
    }).join(',');
  }
  let status: 'active' | 'inactive' | null = null;
  if (filters) {
    for (let i = 0; i < filters.length; i++) {
      if (filters[i].id === 'active' && filters[i].value !== null) {
        status = filters[i].value.value;
      }
    }
  }
  let options = {
    includeItems: true,
    type: UserType.alertType(user.type)[0] || undefined,
    ...params,
    sort: sortParam,
  };
  if (status) {
    options.status = status;
  }

  let {
    data: { results, ...data },
  } = await requester.get<PaginatedData<RawAlert>>(ALERTS_PATH, {
    params: options,
  });
  results = results.filter((alert) => !(alert.isArchived && params.hideArchived));
  return {
    ...data,
    results: results.map((alert) => ProductAlert.fromRaw(alert)),
  };
}

async function getAlert(
  id: ProductAlert['id'],
  includeItems?: Boolean
): Promise<ProductAlert> {
  return !includeItems
    ? ProductAlert.fromRaw(
        (await requester.get<RawAlert>(urljoin(ALERTS_PATH, id.toString())))
          .data
      )
    : ProductAlert.fromRaw(
        (
          await requester.get<RawAlert>(urljoin(ALERTS_PATH, id.toString()), {
            params: { includeItem: true },
          })
        ).data
      );
}

async function downloadAlertFile(
  { sort, filters, ...params }: FetchAlertFileParams,
  user: User
): Promise<any> {
  let sortParam: string | undefined;
  if (sort) {
    /* The sort parameter is provided to the backend as a comma separated list of sort directions, such as:
        'active asc,expiry_date desc,alert_number asc' */
    sortParam = Array.from({
      *[Symbol.iterator]() {
        for (const [key, direction] of sort.entries()) {
          yield `${key} ${direction}`;
        }
      },
    }).join(',');
  }

  let status: 'active' | 'inactive' | null = null;
  if (filters) {
    for (let i = 0; i < filters.length; i++) {
      if (filters[i].id === 'active' && filters[i].value !== null) {
        status = filters[i].value.value;
      }
    }
  }
  let options = {
    includeItems: true,
    type: user.type === UserType.QAAdmin ? 'qa_alert' : 'price_recall',
    ...params,
    sort: sortParam,
  };
  if (status) {
    options.status = status;
  }

  console.log(options);
  
  return requester
    .get<any>(ALERTS_PATH + '/export', {
      headers: {
        'Content-Disposition': 'attachment',
      },
      responseType: 'blob',
      params: options
    })
    .then((file) => {
      download(file.data, exportFileName() + '.' + params.fileType);
      Toast.present(
        { message: 'Alerts have been successfully exported' },
        ToastState.SUCCESS
      );
      return true;
    })
    .catch((err) => {
      Toast.present({ message: 'Could not export alerts' }, ToastState.ERROR);
      return false;
    });
}

async function createAlert(
  alert: Omit<ProductAlert, 'id' | 'clone' | 'expiryDate'> & Partial<Pick<ProductAlert, 'expiryDate'>>
): Promise<ProductAlert> {
  return ProductAlert.fromRaw(
    (await requester.post<RawAlert>(ALERTS_PATH, alert.toRawPost())).data
  );
}

async function savePhoto(file: File) {
  const data = new FormData();
  data.append('photo', file);
  const res = await requester.post(ALERTS_PATH + '/photos', data);
  return res.data;
}

async function updateAlert(
  id: ProductAlert['id'],
  updatedAlert: Partial<Omit<ProductAlert, 'id'>>
): Promise<ProductAlert> {
  return ProductAlert.fromRaw(
    (
      await requester.patch<RawAlert>(urljoin(ALERTS_PATH, id.toString()), {
        ...updatedAlert,
        id: undefined,
      })
    ).data
  );
}

async function fetchProductNames(
  productNumbers: string[]
): Promise<Map<string, string | null>> {
  const { data: itemInfo } = await requester.get<Product[]>(PRODUCTS_PATH, {
    params: { ids: productNumbers },
  });
  return new Map({
    *[Symbol.iterator]() {
      for (const { itemName, itemNumber } of itemInfo) {
        yield [itemNumber, itemName];
      }
    },
  });
}

async function fetchProductInfos(ids: string[]): Promise<ProductInfo[]> {
  const { data: itemInfo } = await requester.get<ProductInfo[]>(PRODUCTS_PATH, {
    params: { ids },
  });

  return itemInfo;
}

function exportFileName() {
  let today = new Date();
  let year = today.getFullYear();
  let day = addLeadingZero(today.getDate());
  let month = addLeadingZero(today.getMonth() + 1);
  let hour = addLeadingZero(today.getHours());
  let minute = addLeadingZero(today.getMinutes());
  let seconds = addLeadingZero(today.getSeconds());
  let dateName = `${year}-${month}-${day}__${hour}.${minute}.${seconds}EST`;
  return `Grocer_Alerts_Export__${dateName}`;
}

function addLeadingZero(time: Number) {
  let str = time.toString();
  if (str.length < 2) return '0' + str;
  return str;
}

export {
  getAlerts,
  getAlert,
  createAlert,
  savePhoto,
  updateAlert,
  fetchProductNames,
  fetchProductInfos,
  downloadAlertFile,
  ALERTS_PATH,
  AlertSortSpecification,
  alertSortAllowedKeys,
  AlertSortAllowedKey,
};



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/services/ProductAlertService.ts